import type * as MixpanelSdk from 'mixpanel-browser/dist/mixpanel-core.cjs';
import { env } from '@/config/env';
import type { Locale } from '@/i18n/translations';

export type PublicPage =
  'home' | 'project' | 'organization' | 'app_fallback' | 'privacy' | 'unknown';

export type PublicAnalyticsEvent =
  | 'Public Page Viewed'
  | 'Public Project Viewed'
  | 'Public Organization Viewed'
  | 'Public Project Selected'
  | 'Public Donation Clicked'
  | 'Public App Open Clicked'
  | 'Public App Download Clicked'
  | 'Public Language Changed'
  | 'Public Content Load Failed'
  | 'Public Content Retry Clicked';

export interface AnalyticsProperties {
  page?: PublicPage;
  language?: Locale;
  previous_language?: Locale;
  project_id?: string;
  organization_id?: string;
  fund_type?: string;
  placement?:
    | 'project_card'
    | 'donate_card'
    | 'mobile_bar'
    | 'fund_card'
    | 'home'
    | 'app_fallback'
    | 'footer';
  destination?: 'native_app' | 'app_fallback' | 'web_checkout';
  platform?: 'ios' | 'android';
  content?: 'project' | 'organization' | 'projects' | 'funds' | 'privacy';
  status?: number;
}

export const publicPage = (pathname: string): PublicPage => {
  if (pathname === '/') return 'home';
  if (/^\/p\/[^/]+\/?$/.test(pathname)) return 'project';
  if (/^\/o\/[^/]+\/?$/.test(pathname)) return 'organization';
  if (pathname === '/app' || /^\/organizations\/[^/]+\/projects\/[^/]+\/?$/.test(pathname))
    return 'app_fallback';
  if (pathname === '/privacy') return 'privacy';
  return 'unknown';
};

let initialized = false;
let loading: Promise<typeof MixpanelSdk> | undefined;
let queued = 0;

export const trackPublicEvent = (
  event: PublicAnalyticsEvent,
  properties: AnalyticsProperties,
): void => {
  const token = env.mixpanelToken;
  if (token === null) return;

  if (queued >= 50) return;
  queued += 1;
  loading ??= import('mixpanel-browser/dist/mixpanel-core.cjs');
  void loading
    .then(({ default: mixpanel }) => {
      if (!initialized) {
        // track_marketing is supported by the SDK but absent from its bundled Config type.
        const config = {
          api_host: env.mixpanelApiHost,
          autocapture: false,
          track_pageview: false,
          record_sessions_percent: 0,
          remote_settings_mode: 'disabled' as const,
          disable_persistence: true,
          batch_requests: false,
          ip: false,
          ignore_dnt: false,
          track_marketing: false,
          stop_utm_persistence: true,
          store_google: false,
          skip_first_touch_marketing: true,
          save_referrer: false,
          property_blacklist: [
            '$current_url',
            '$referrer',
            '$referring_domain',
            '$initial_referrer',
            '$initial_referring_domain',
            '$search_engine',
            'mp_keyword',
          ],
        };
        mixpanel.init(token, config);
        initialized = true;
      }
      // Send immediately, including when a link leaves the site or opens the native app.
      mixpanel.track(
        event,
        { ...properties, source: 'public_website', environment: import.meta.env.MODE },
        { transport: 'sendBeacon', send_immediately: true },
      );
    })
    .catch(() => undefined)
    .finally(() => {
      queued -= 1;
    });
};
